import {CommonModule} from '@angular/common';
import {ChangeDetectionStrategy, Component, EventEmitter, Output} from '@angular/core';
import {
    AbstractControl,
    FormArray,
    FormBuilder,
    FormControl,
    FormGroup,
    ReactiveFormsModule,
    ValidationErrors,
    Validators,
} from '@angular/forms';
import {ActivatedRoute} from '@angular/router';
import {CheckboxGroupComponent, ControlMessagesComponent} from '@brunata-angular/ui';
import {InputDateGroupComponent} from '@brunata-angular/ui';
import {DateTime} from 'luxon';
import {map, Observable, switchMap} from 'rxjs';
import {ConsumptionReportService} from '../consumption-report.service';
import {Criteria} from '../types/criteria.model';
import {MeterAggregation} from '../types/device-type.model';
import {AppValidators, keyComparator, TranslatePipe} from '@brunata-angular/util';

export type DeviceTypeControl = FormGroup<{
    allocationUnit: FormControl<string>;
    selected: FormControl<boolean>;
    meterType: FormControl<number>;
    count: FormControl<number | null>;
    unit: FormControl<number | null>;
}>;
type SearchFormControl = FormGroup<{
    buildingNo: FormControl<string | null>;
    startDate: FormControl<DateTime | null>;
    endDate: FormControl<DateTime | null>;
    deviceTypes: FormArray<DeviceTypeControl>;
    periodInterval: FormControl<string | null>;
    groupLevel: FormControl<string | null>;
}>;

@Component({
    selector: 'app-consumption-report-search-criteria',
    templateUrl: './consumption-report-search-criteria.component.html',
    styleUrl: './consumption-report-search-criteria.component.scss',
    imports: [
        CommonModule,
        TranslatePipe,
        ReactiveFormsModule,
        InputDateGroupComponent,
        CheckboxGroupComponent,
        ControlMessagesComponent,
    ],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ConsumptionReportSearchCriteriaComponent {
    @Output() searchCriteria = new EventEmitter<Criteria>();

    periodIntervals: {
        periodInterval: string;
        label: string;
    }[] = [
        {
            periodInterval: 'M',
            label: 'monthly',
        },
        {
            periodInterval: 'Y',
            label: 'annualy',
        },
        {
            periodInterval: 'E',
            label: 'selected-period-interval',
        },
    ];

    groupLevels: {
        groupLevel: string;
        label: string;
    }[] = [
        {
            groupLevel: 'M',
            label: 'meter',
        },
        {
            groupLevel: 'L',
            label: 'apartment',
        },
        {
            groupLevel: 'B',
            label: 'system',
        },
    ];

    readonly searchFormControl$: Observable<SearchFormControl> = this.route.paramMap.pipe(
        switchMap(paramMap =>
            this.consumptionReportService.getMeterAggregations(paramMap.get('buildingno')).pipe(
                map(deviceTypes => {
                    const formGroup = this.formBuilder.group({
                        buildingNo: paramMap.get('buildingno'),
                        startDate: new FormControl<DateTime | null>(null, [
                            Validators.required,
                            AppValidators.maxDate(DateTime.now().startOf('day')),
                        ]),
                        endDate: new FormControl<DateTime | null>(null, [Validators.required]),
                        deviceTypes: new FormArray<DeviceTypeControl>(
                            deviceTypes
                                .filter(it => it.allocationUnit !== 'P')
                                .sort(keyComparator(a => a.allocationUnitSortOrder))
                                .map(it => this.createDeviceTypeForm(it)),
                            ConsumptionReportSearchCriteriaComponent.minimumOneTypeSelected,
                        ),
                        periodInterval: new FormControl<string | null>(null, Validators.required),
                        groupLevel: new FormControl<string | null>(null, Validators.required),
                    });
                    formGroup.addValidators(
                        ConsumptionReportSearchCriteriaComponent.endDateAfterStartDate,
                    );
                    return formGroup;
                }),
            ),
        ),
    );

    constructor(
        private formBuilder: FormBuilder,
        private route: ActivatedRoute,
        private consumptionReportService: ConsumptionReportService,
    ) {}

    private createDeviceTypeForm(meterAggregation: MeterAggregation): FormGroup {
        return this.formBuilder.group({
            allocationUnit: meterAggregation.allocationUnit,
            selected: false,
            count: meterAggregation.count,
            meterType: meterAggregation.meterType,
            unit: meterAggregation.unit,
        });
    }

    clearForm(formGroup: SearchFormControl): void {
        formGroup.patchValue({
            buildingNo: formGroup.value.buildingNo,
            startDate: DateTime.now().minus({months: 1}),
            endDate: null,
            deviceTypes: formGroup.getRawValue().deviceTypes.map(it => ({...it, selected: false})),
            periodInterval: null,
            groupLevel: null,
        });
        formGroup.markAsUntouched();
    }

    submit(formGroup: SearchFormControl): void {
        formGroup.markAllAsTouched();
        if (formGroup.valid) {
            const value = formGroup.getRawValue();
            const criteria: Criteria = {
                buildingNo: value.buildingNo,
                startDate: value.startDate,
                endDate: value.endDate,
                deviceTypes: value.deviceTypes
                    .filter(deviceType => deviceType.selected)
                    .map(deviceType => ({
                        allocationUnit: deviceType.allocationUnit,
                        meterType: deviceType.meterType,
                        unit: deviceType.unit,
                    })),
                periodInterval: value.periodInterval,
                groupLevel: value.groupLevel,
            };
            this.searchCriteria.emit(criteria);
        }
    }

    static minimumOneTypeSelected(control: AbstractControl): ValidationErrors | null {
        const formArray = control as FormArray<DeviceTypeControl>;
        return !formArray.controls.some(it => it.value.selected === true) ? {required: true} : null;
    }

    static endDateAfterStartDate(control: AbstractControl): ValidationErrors | null {
        const startDate = control.get('startDate')?.value as DateTime | null;
        const endDate = control.get('endDate')?.value as DateTime | null;

        const startDateValid = startDate != null && startDate.isValid;
        const endDateValid = endDate != null && endDate.isValid;

        if (!startDateValid) {
            return {startDateInvalid: {value: startDate}};
        }

        if (!endDateValid) {
            return {endDateInvalid: {value: endDate}};
        }

        if (startDate.toMillis() >= endDate.toMillis()) {
            return {startDateAfterEndDate: {startDate, endDate}};
        }

        return null;
    }
}
